import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import AutocompleteSelectInput from "veneer/AutocompleteSelectInput";
import Button from "veneer/Button";
import FilePicker from "veneer/FilePicker";
import Stack from "veneer/Stack";
import Typography from "veneer/Typography";

import { POSSIBLE_CSV_MIMETYPES } from "cortex/ui/src/constant/app";
import { devicePropertyOptions } from "cortex/ui/src/fleet/constant/deviceDetails";
import { flagOptions } from "cortex/ui/src/fleet/constant/flags";
import { Application } from "cortex/ui/src/fleet/view/application/model";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { useAppSelector } from "cortex/ui/src/hooks";
import {
  applicationsSelector,
  getAppParamOptions,
  installedAppsSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import {
  devicesSelector,
  getTargets,
} from "cortex/ui/src/redux-store/devices/selectors";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import { deviceService } from "cortex/ui/src/service/device";
import BackButton from "cortex/ui/src/ui/button";
import { Features } from "cortex/ui/src/ui/features";
import Notification from "cortex/ui/src/ui/notification";
import SegmentedControl, {
  SegmentedControlButton,
} from "cortex/ui/src/ui/segmented-control";
import { Option } from "cortex/ui/src/ui/table/types";
import type { ResponseLike } from "cortex/ui/src/util/response";
import {
  getErrorDetails,
  getErrorMessage,
  getFormErrors,
} from "cortex/ui/src/util/response";
import { Container } from "cortex/ui/src/view/layout";
import { socket } from "cortex/ui/src/websocket";

import BulkDeviceEditSample from "./BulkDeviceEditSample";

interface FormErrors {
  [key: string]: string | undefined;
  file?: string;
}

interface FormValues {
  appParams: Option[];
  file: File | undefined;
  flags: Option[];
  isCustomConfig: boolean;
  properties: Option[];
  targets: Option[];
}

interface UploadResponseItem {
  message: string;
}

const getAppsAsArray = makeGetAllAsArray<Application>();
const getDevicesAsArray = makeGetAllAsArray<Device>();
function getDefaultFormValues(): FormValues {
  return {
    appParams: [],
    file: undefined,
    flags: [],
    isCustomConfig: false,
    properties: [],
    targets: [],
  };
}

export default function BulkEditView() {
  const appsById = useAppSelector(applicationsSelector);
  const devicesById = useAppSelector(devicesSelector);

  const appParamOptions = useMemo(() => {
    const apps = getAppsAsArray({ byId: appsById });
    const installedApps = installedAppsSelector(apps);
    return getAppParamOptions(installedApps) as Option[];
  }, [appsById]);

  const targetOptions = useMemo(() => {
    const devices = getDevicesAsArray({ byId: devicesById });
    return getTargets(devices, true) as Option[];
  }, [devicesById]);

  const [errorDetails, setErrorDetails] = useState<ReactNode>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [form, setForm] = useState<FormValues>(getDefaultFormValues);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [successMessage, setSuccessMessage] = useState<ReactNode>("");
  const [uploading, setUploading] = useState<boolean>(false);

  const bulkEditDevices = useCallback(async (file: File) => {
    const resp = await deviceService.bulkEdit(file, socket.id);
    return resp.data.data as UploadResponseItem[];
  }, []);

  const resetMessages = useCallback(() => {
    setFormErrors({});
    setErrorDetails("");
    setErrorMessage("");
    setSuccessMessage("");
  }, []);

  const handleSuccess = useCallback(
    (response: UploadResponseItem[]) => {
      resetMessages();
      setSuccessMessage(
        <div>
          <ul>
            {response.map((r, i) => (
              <li key={i}>{r.message}</li>
            ))}
          </ul>
          <span>
            Note that offline devices will reflect any changes when a device is
            back online.
            <br />
            For any flag updates, the device must be restarted for changes to be
            implemented.
          </span>
        </div>,
      );
    },
    [resetMessages],
  );

  const isValidFileType = useCallback((filetype?: string) => {
    return (
      !filetype || POSSIBLE_CSV_MIMETYPES.some((mime) => mime === filetype)
    );
  }, []);

  const onFileChange = useCallback(
    (file: File | null) => {
      setForm((prev) => ({ ...prev, file: undefined }));
      resetMessages();
      if (file && !isValidFileType(file.type)) {
        setFormErrors({ file: "The selected file must be a .csv." });
        return;
      }
      setForm((prev) => ({ ...prev, file: file ?? undefined }));
    },
    [resetMessages],
  );

  const uploadAction = useCallback(
    async (file: File) => {
      try {
        const response = await bulkEditDevices(file);
        handleSuccess(response);
      } catch (resp) {
        const response = resp as ResponseLike | null;
        const details = getErrorDetails(response);
        const message = details ? "Error details" : getErrorMessage(response);
        const nextFormErrors =
          (
            getFormErrors(response ?? undefined) as {
              formErrors?: FormErrors;
            }
          ).formErrors ?? {};

        setErrorDetails(details);
        setErrorMessage(message);
        setFormErrors(nextFormErrors);
      } finally {
        setUploading(false);
      }
    },
    [bulkEditDevices, handleSuccess],
  );

  const startUpload = useCallback(
    async (file: File) => {
      resetMessages();
      setUploading(true);

      await uploadAction(file);
    },
    [resetMessages, uploadAction],
  );

  const submit = useCallback(() => {
    const selectedFile = form.file;
    if (!selectedFile) {
      setFormErrors({ file: "Please select a file." });
      return;
    }

    if (!isValidFileType(selectedFile.type)) {
      setFormErrors({ file: "A valid .csv file is required." });
      return;
    }

    void startUpload(selectedFile);
  }, [form.file, isValidFileType, startUpload]);

  const toggleCustomConfig = useCallback(
    (value: boolean) => {
      const nextTargets = value
        ? targetOptions.filter((target) => target.label === "tag: _all")
        : [];

      resetMessages();
      setForm((prev) => ({
        ...prev,
        appParams: [],
        flags: [],
        isCustomConfig: value,
        properties: [],
        targets: nextTargets,
      }));
    },
    [resetMessages, targetOptions],
  );

  const isBulkEditEnabled = Features.isEnabled(
    Features.constants.DEVICE_BULK_EDIT,
  );

  const selectedOpts = useMemo(
    () => ({
      appParams: form.appParams,
      flags: form.flags,
      isCustomConfig: form.isCustomConfig,
      properties: form.properties,
      targets: form.targets.map((target) => String(target.value)),
    }),
    [
      form.appParams,
      form.flags,
      form.isCustomConfig,
      form.properties,
      form.targets,
    ],
  );

  return (
    <Container p={1} spacing={2} title="Bulk Edit" width="50%">
      {isBulkEditEnabled && (
        <SegmentedControl
          dataTestId="segmented-control"
          label="Choose from the two options below"
          labelBold={true}
          onChange={(value?: string) => {
            toggleCustomConfig(value === "true");
          }}
        >
          <SegmentedControlButton selected={!form.isCustomConfig} value="false">
            Export Device Configuration
          </SegmentedControlButton>
          <SegmentedControlButton selected={form.isCustomConfig} value="true">
            Export Custom Device Configuration
          </SegmentedControlButton>
        </SegmentedControl>
      )}
      {form.isCustomConfig && (
        <>
          <Typography fontWeight="bold">
            Select the columns and rows you would like to edit
          </Typography>
          <AutocompleteSelectInput
            inputProps={{ label: "Device Properties (Columns)" }}
            isOptionEqualToValue={(option: Option, value: Option) =>
              option.value === value.value
            }
            multiple
            onChange={(_e: unknown, opt: Option[]) =>
              setForm((prev) => ({ ...prev, properties: opt }))
            }
            options={devicePropertyOptions}
            size="small"
            value={form.properties}
          />
          <AutocompleteSelectInput
            inputProps={{ label: "Application Parameters (Columns)" }}
            isOptionEqualToValue={(option: Option, value: Option) =>
              option.value === value.value
            }
            multiple
            onChange={(_e: unknown, opt: Option[]) =>
              setForm((prev) => ({ ...prev, appParams: opt }))
            }
            options={appParamOptions}
            size="small"
            value={form.appParams}
          />
          <AutocompleteSelectInput
            inputProps={{ label: "Device Flags (Columns)" }}
            isOptionEqualToValue={(option: Option, value: Option) =>
              option.value === value.value
            }
            multiple
            onChange={(_e: unknown, opt: Option[]) =>
              setForm((prev) => ({ ...prev, flags: opt }))
            }
            options={flagOptions}
            size="small"
            value={form.flags}
          />
          <AutocompleteSelectInput
            inputProps={{ label: "Target Networks & Devices (Rows)" }}
            isOptionEqualToValue={(option: Option, value: Option) =>
              option.value === value.value
            }
            multiple
            onChange={(_e: unknown, opt: Option[]) =>
              setForm((prev) => ({ ...prev, targets: opt }))
            }
            options={targetOptions}
            size="small"
            value={form.targets}
          />
        </>
      )}
      <Notification
        error={errorMessage}
        errorChildren={errorDetails}
        success={successMessage}
        successChildren={<Link to="/devices">Device List</Link>}
      />
      <BulkDeviceEditSample {...selectedOpts} />
      {Object.values(formErrors).map(
        (error, index) =>
          error && (
            <Alert key={index} severity="error">
              {error}
            </Alert>
          ),
      )}
      <Stack alignItems="center" direction="row" spacing={1}>
        <FilePicker
          accept={POSSIBLE_CSV_MIMETYPES}
          onChange={onFileChange}
          variant="contained"
        >
          Upload .csv file
        </FilePicker>
        <Typography variant="body2">
          {form.file ? form.file.name : "No file selected"}
        </Typography>
      </Stack>
      <Typography variant="caption">
        Select the .csv file of devices you want to edit.
      </Typography>
      <Stack alignItems="center" direction="row" gap={1}>
        <BackButton />
        <Button disabled={uploading} onClick={submit}>
          Save
        </Button>
      </Stack>
    </Container>
  );
}
